import { useEffect, useRef } from "react";

interface TurnstileApi {
  render: (el: HTMLElement, opts: Record<string, unknown>) => string;
  remove: (id: string) => void;
}
declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

let scriptPromise: Promise<void> | null = null;
function loadTurnstile(): Promise<void> {
  if (window.turnstile) return Promise.resolve();
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const s = document.createElement("script");
      s.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
      s.async = true;
      s.defer = true;
      s.onload = () => resolve();
      s.onerror = () => reject(new Error("turnstile_load_failed"));
      document.head.appendChild(s);
    });
  }
  return scriptPromise;
}

/**
 * Cloudflare Turnstile widget. Renders nothing without a sitekey.
 * The childElementCount guard makes it safe against React StrictMode's dev
 * double-mount (so the widget isn't rendered twice into the same element).
 */
export function Turnstile({ sitekey, onToken }: { sitekey: string; onToken: (t: string) => void }) {
  const ref = useRef<HTMLDivElement>(null);
  const cb = useRef(onToken);
  cb.current = onToken;

  useEffect(() => {
    const container = ref.current;
    if (!sitekey || !container) return;
    let widgetId: string | null = null;
    let removed = false;

    loadTurnstile()
      .then(() => {
        if (removed || !window.turnstile || container.childElementCount > 0) return;
        widgetId = window.turnstile.render(container, {
          sitekey,
          callback: (t: string) => cb.current(t),
          "error-callback": () => cb.current(""),
          "expired-callback": () => cb.current(""),
        });
      })
      .catch(() => cb.current(""));

    return () => {
      removed = true;
      if (widgetId && window.turnstile) {
        try {
          window.turnstile.remove(widgetId);
        } catch {
          /* ignore */
        }
      }
    };
  }, [sitekey]);

  if (!sitekey) return null;
  return <div ref={ref} className="flex justify-center" />;
}
